<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Customizations/Typer_Customization';
    let nextLink= '/Documentation/Customizations/Other_Customizations';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="customization-of-the-validator-1"
  bind:intersecting={visibleById["customization-of-the-validator-1"]}
  onIntersectChange={(visible) => setVisible("customization-of-the-validator-1", visible)}
>Customization of the Validator</SectionComponent>
<p>The validator can be adjusted by adding a single Typescript class. This class should implement
the generated <a
  href="/Documentation/Under_the_Hood/FreTool_Interfaces#frevalidator-4"
>checker interface</a> of the validator,
adding checks per node of the AST that is visited.  </p>
<SectionComponent tag="h2" 
  id="the-checker-interface-2"
  bind:intersecting={visibleById["the-checker-interface-2"]}
  onIntersectChange={(visible) => setVisible("the-checker-interface-2", visible)}
>The Checker Interface</SectionComponent>
<p>For each language a checker interface is generated. This interface is an extension of the worker
part of the visitor pattern, adding an error list. </p>
<pre class="language-ts"><code class="language-ts">// CustomizationsProject/src/freon/validator/gen/EntityModelValidator.ts#L38-L40

</code></pre>
<p>The worker defines two methods for each concept in the language, as shown in the next example.</p>
<pre class="language-ts"><code class="language-ts">// CustomizationsProject/src/freon/utils/gen/EntityModelWorker.ts#L48-L57

</code></pre>
<SectionComponent tag="h2" 
  id="the-validation-class-3"
  bind:intersecting={visibleById["the-validation-class-3"]}
  onIntersectChange={(visible) => setVisible("the-validation-class-3", visible)}
>The Validation Class</SectionComponent>
<p>As a convenience, Freon generates a file <code>~/freon/validator/CustomYourLanguageNameValidator.ts</code>,
which will not be overwritten upon regeneration. This validator should implement the checker interface.</p>
<p>You can give a custom implementation for any of the methods of the default worker. This method will
override the (empty) default implementation.
Any validation errors should be pushed onto the <code>errorList</code> attribute. Note that each error must implement the
<a
  href="/Documentation/Under_the_Hood/FreTool_Interfaces#frevalidator-4"
>FreError</a> interface.</p>
<p>The result of the above could look something like the following. In this example only nodes of
type <code>EntityFunction</code> are checked. If the name of the node equals <code>determine</code> than an error is
pushed on the <code>errorList</code>. If you want the walker to stop when an erroneous node is found you
should return <code>true</code>, else return <code>false</code>.</p>
<pre class="language-ts"><code class="language-ts">// CustomizationsProject/src/custom/validator/CustomEntityModelValidator.ts

// Generated by the Freon Language Generator.
import &#123; FreError, FreErrorSeverity &#125; from "@freon4dsl/core";
import &#123; EntityModelDefaultWorker &#125; from "../utils/gen/EntityModelDefaultWorker.js";
import &#123; EntityModelCheckerInterface &#125; from "./gen/EntityModelValidator.js";
import &#123;EntityFunction&#125; from "../language/gen/index.js";

export class CustomEntityModelValidator extends EntityModelDefaultWorker implements EntityModelCheckerInterface &#123;
    errorList: FreError[] = [];

    /**
     * Example of a custom validation. Every EntityFunction with name 'determine' is considered incorrect.
     * @param modelelement
     */
    public execBeforeEntityFunction(modelelement: EntityFunction): boolean &#123;
        if (modelelement.name == "determine") &#123;
            this.errorList.push(
                new FreError(
                    `"determine" is a terrible name for a Function`,
                    modelelement,
                    modelelement.name,
                    FreErrorSeverity.Error
                )
            );
            return true;
        &#125;
        return false;
    &#125;
&#125;
</code></pre>


<PrevNextSection {prevLink} {nextLink} />